import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MarketingService, { type AcquisitionReport, type ReportBy } from '../../services/marketing/marketingService.ts';
import { RANGE_LABELS, type RangeKey, rangeFor } from '../credit/dateRanges.ts';
import { errorMessage, euro, percent } from './marketingShared.ts';

const service = new MarketingService();
const BY: ReportBy[] = ['source', 'campaign', 'partner', 'channel'];
const num = { fontVariantNumeric: 'tabular-nums' } as const;

/**
 * "Where customers come from": the customers who registered in the range,
 * grouped by their first touch (a link, a QR code, an advert, a partner), and
 * what they went on to order. The server counts each group's delivered orders
 * up to today, so an older range keeps growing as its customers keep ordering.
 */
const AcquisitionReportPage = () => {
    const { t } = useTranslation();
    const initial = rangeFor('thisMonth');
    const [range, setRange] = useState<RangeKey | null>('thisMonth');
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [by, setBy] = useState<ReportBy>('source');
    const [report, setReport] = useState<AcquisitionReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState<string | null>(null);

    useEffect(() => {
        if (!from || !to) return;
        let cancelled = false;
        setLoading(true);
        setFailed(null);
        service.report(from, to, by)
            .then((r) => { if (!cancelled) setReport(r); })
            .catch((e) => {
                if (cancelled) return;
                // Never show a failed report as zeroes.
                setReport(null);
                setFailed(errorMessage(e, t('marketing.report.loadFailed')));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [from, to, by, t]);

    const applyRange = (key: RangeKey | null) => {
        setRange(key);
        if (key) {
            const r = rangeFor(key);
            setFrom(r.from);
            setTo(r.to);
        }
    };

    const totals = report?.totals;
    const totalRate = totals && totals.signups > 0 ? totals.ordered / totals.signups : 0;

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <TravelExploreIcon />
                <Typography variant="h5">{t('resources.acquisitionReport')}</Typography>
            </Stack>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Stack spacing={2}>
                        <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, v) => applyRange(v)} sx={{ flexWrap: 'wrap' }}>
                            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
                                <ToggleButton key={key} value={key}>{t(`marketing.ranges.${key}`)}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                            <TextField label={t('marketing.report.from')} type="date" size="small" value={from}
                                onChange={(e) => { setFrom(e.target.value); setRange(null); }}
                                InputLabelProps={{ shrink: true }} />
                            <TextField label={t('marketing.report.to')} type="date" size="small" value={to}
                                onChange={(e) => { setTo(e.target.value); setRange(null); }}
                                InputLabelProps={{ shrink: true }} />
                            <Typography variant="body2" color="text.secondary">{t('marketing.report.by')}</Typography>
                            <ToggleButtonGroup size="small" exclusive value={by} onChange={(_, v) => v && setBy(v)}>
                                {BY.map((b) => (
                                    <ToggleButton key={b} value={b}>{t(`marketing.report.byOptions.${b}`)}</ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {failed && <Alert severity="error" sx={{ mb: 2 }}>{failed}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : report && (
                <Card>
                    <CardContent>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 720 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t(`marketing.report.byOptions.${report.by ?? by}`)}</TableCell>
                                        <TableCell align="right">{t('marketing.report.signups')}</TableCell>
                                        <TableCell align="right">{t('marketing.report.ordered')}</TableCell>
                                        <TableCell align="right">{t('marketing.report.firstOrderRate')}</TableCell>
                                        <TableCell align="right">{t('marketing.report.deliveredOrders')}</TableCell>
                                        <TableCell align="right">{t('marketing.report.foodSales')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Typography variant="body2" color="text.secondary">{t('marketing.report.empty')}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {report.rows.map((r) => (
                                        <TableRow key={r.key} hover>
                                            <TableCell>{r.key}</TableCell>
                                            <TableCell align="right" sx={num}>{r.signups}</TableCell>
                                            <TableCell align="right" sx={num}>{r.ordered}</TableCell>
                                            <TableCell align="right" sx={num}>{percent(r.firstOrderRate)}</TableCell>
                                            <TableCell align="right" sx={num}>{r.deliveredOrders}</TableCell>
                                            <TableCell align="right" sx={num}>{euro(r.foodSales)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {totals && (
                                        <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid var(--color-secondary)' } }}>
                                            <TableCell>{t('marketing.report.total')}</TableCell>
                                            <TableCell align="right" sx={num}>{totals.signups}</TableCell>
                                            <TableCell align="right" sx={num}>{totals.ordered}</TableCell>
                                            <TableCell align="right" sx={num}>{percent(totalRate)}</TableCell>
                                            <TableCell align="right" sx={num}>{totals.deliveredOrders}</TableCell>
                                            <TableCell align="right" sx={num}>{euro(totals.foodSales)}</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('marketing.report.note')}</Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default AcquisitionReportPage;
